// src/pages/OcfEpisode.jsx
// Pagina-episodio del pillar OCF, risolta dinamicamente da /:lang/ocf/:materia/:slug.
// Per gli episodi futuri mostra un badge "Disponibile dal <data>". Bilingue via LangContext.
import React, { useContext } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import "./OcfHub.css";
import Seo from "../components/Seo";
import YouTubeEmbed from "../components/YouTubeEmbed";
import { LangContext } from "../lang/LangProvider";
import { trackVideoPlay } from "../hooks/useAnalytics";
import { withUtm } from "../utils/utm";
import { getOcfEpisode } from "../data/ocfEpisodes";
import { OCF_EPISODE_BODIES } from "./ocfEpisodeBodies";

function L(lang, it, en) {
  return lang === "en" ? en : it;
}

// Etichetta tab del tool (la pagina Tools usa tab interni, l'URL resta /tools).
const TOOL_LABEL = {
  etf: { it: "Simulatore ETF", en: "ETF Simulator" },
  compound: { it: "Calcolatore Obiettivi", en: "Goal Planner" },
};

export default function OcfEpisode() {
  const { lang } = useContext(LangContext);
  const { materia, slug } = useParams();

  // OCF è un ente esclusivamente italiano: redirect delle pagine EN alla versione IT.
  if (lang !== "it") return <Navigate to={`/it/ocf/${materia}/${slug}`} replace />;

  const found = getOcfEpisode(materia, slug);
  // Slug inesistente → torna all'hub (evita pagine vuote indicizzabili).
  if (!found) return <Navigate to={`/${lang}/ocf`} replace />;

  const { subject, episode: ep } = found;
  const title = L(lang, ep.titleIt, ep.titleEn);
  const intro = L(lang, ep.introIt || "", ep.introEn || "");
  const hubTo = `/${lang}/ocf`;

  const releaseDate = ep.availableFrom ? new Date(ep.availableFrom) : null;
  const isFuture = releaseDate && !isNaN(releaseDate) && releaseDate > new Date();
  const releaseFmt = releaseDate
    ? releaseDate.toLocaleDateString(lang === "en" ? "en-GB" : "it-IT", { day: "numeric", month: "long", year: "numeric" })
    : null;

  const tool = ep.tool ? TOOL_LABEL[ep.tool] : null;
  const toolsHref = withUtm(`/${lang}/tools`, {
    source: "site",
    medium: "article",
    campaign: `ocf-ep${ep.n}`,
  });

  // VideoObject: l'embed è click-to-load (nessun iframe nel prerender), quindi
  // dichiariamo il video con structured data così è indicizzabile dai motori.
  const thumbUrl = `https://i.ytimg.com/vi/${ep.videoId}/hqdefault.jpg`;
  const videoJsonLd = {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: `Esame OCF — Ep. ${ep.n}: ${title}`,
    description: intro || title,
    thumbnailUrl: [thumbUrl],
    embedUrl: `https://www.youtube-nocookie.com/embed/${ep.videoId}`,
    contentUrl: `https://www.youtube.com/watch?v=${ep.videoId}`,
    ...(ep.availableFrom ? { uploadDate: ep.availableFrom } : {}),
  };

  return (
    <>
      <Seo
        title={`Esame OCF — Ep. ${ep.n}: ${title}`}
        description={intro || title}
        path={`/${lang}/ocf/${subject.path}/${slug}`}
        type="article"
        image={thumbUrl}
        jsonLd={videoJsonLd}
      />

      <div className="ocf-hub">
        <header className="ocf-hub-head">
          <p className="ocf-eyebrow">
            <Link to={hubTo}>{L(lang, "← Hub Esame OCF", "← OCF Exam Hub")}</Link>
            {" · "}
            {L(lang, subject.titleIt, subject.titleEn)}
          </p>
          <h1>{L(lang, "Esame OCF", "OCF Exam")} — Ep. {ep.n}: {title}</h1>
          {isFuture && (
            <p className="ocf-release-badge">
              {L(lang, `Disponibile dal ${releaseFmt}`, `Available from ${releaseFmt}`)}
            </p>
          )}
          {intro && <p className="ocf-hub-intro">{intro}</p>}
        </header>

        <YouTubeEmbed
          videoId={ep.videoId}
          title={`Esame OCF -${ep.n}- ${title}`}
          onPlay={(id) => trackVideoPlay(id, "article")}
        />

        {OCF_EPISODE_BODIES[slug] && (
          <section className="ocf-body">{OCF_EPISODE_BODIES[slug]}</section>
        )}

        <section className="ocf-hub-links">
          <p>
            {L(lang, "Torna all'", "Back to the ")}
            <Link to={hubTo}>{L(lang, "Hub Esame OCF", "OCF Exam Hub")}</Link>
            {tool ? (
              <>
                {L(lang, " per la playlist completa, oppure prova il ", " for the full playlist, or try the ")}
                <a href={toolsHref}>{L(lang, tool.it, tool.en)}</a>.
              </>
            ) : (
              L(lang, " per la playlist completa.", " for the full playlist.")
            )}
          </p>
        </section>

        <p className="ocf-hub-disclaimer">
          {L(
            lang,
            "Contenuto a scopo esclusivamente educativo. Non costituisce consulenza personalizzata né raccomandazione.",
            "Educational content only. Not personalized advice or a recommendation."
          )}
        </p>
      </div>
    </>
  );
}
